import React, { createContext, useState, useContext, useEffect } from 'react';
import { postData, refreshAuthToken as apiRefreshToken } from '../func/api';

// 認証コンテキストの作成
export const AuthContext = createContext(null);

// カスタムフックの作成（コンテキストを簡単に使うため）
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  // 状態管理
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // ログイン関数
  const login = async (email, password) => {
    try {
      setLoading(true);
      setError('');

      const userData = await postData('api/auth/login', { email, password });
      
      // トークンを保存
      localStorage.setItem('accessToken', userData.access_token);
      localStorage.setItem('refreshToken', userData.refresh_token);
      
      // ユーザー情報をローカルストレージに保存（セッション復元のため）
      localStorage.setItem('userId', userData.user_id);
      localStorage.setItem('userName', userData.name);
      localStorage.setItem('userRole', userData.role);
      localStorage.setItem('userRoleId', userData.role_id);
      localStorage.setItem('userDepartment', userData.department);
      
      // ユーザー情報をStateに設定
      setCurrentUser({
        id: userData.user_id,
        name: userData.name,
        role: userData.role,
        role_id: userData.role_id,
        department: userData.department
      });
      
      return userData;
    } catch (err) {
      // エラー内容に応じた処理が可能に
      if (err.status === 401) {
        setError('メールアドレスまたはパスワードが正しくありません');
      } else if (err.status >= 500) {
        setError('サーバーエラーが発生しました。後ほど再度お試しください');
      } else {
        setError(err.message || 'ログインに失敗しました');
      }
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // ログアウト関数
  const logout = async () => {
    try {
      setLoading(true);
      
      // API関数を使用してサーバーへログアウトリクエストを送信
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        await postData('api/auth/logout', { refresh_token: refreshToken });
      }
    } catch (err) {
      console.error('ログアウトエラー:', err);
      // ログアウトエラーはUI上で表示しない（ユーザーを混乱させないため）
    } finally {
      // ローカルストレージからすべての認証情報をクリア
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userId');
      localStorage.removeItem('userName');
      localStorage.removeItem('userRole');
      localStorage.removeItem('userRoleId');
      localStorage.removeItem('userDepartment');
      
      // ユーザー情報をリセット
      setCurrentUser(null);
      setError('');
      setLoading(false);
    }
  };

  // トークン更新関数
  const refreshToken = async () => {
    try {
      // api.jsの関数を利用してトークンを更新
      await apiRefreshToken();
      
      // 更新されたトークンに基づいてユーザー情報を更新
      const userId = localStorage.getItem('userId');
      const userName = localStorage.getItem('userName');
      const userRole = localStorage.getItem('userRole');
      const userRoleId = localStorage.getItem('userRoleId');
      const userDepartment = localStorage.getItem('userDepartment');
      
      if (userId && userName) {
        setCurrentUser({
          id: userId,
          name: userName,
          role: userRole,
          role_id: userRoleId,
          department: userDepartment
        });
      }
      
      return true;
    } catch (err) {
      console.error('トークン更新エラー:', err);
      // トークン更新失敗時はログアウト
      await logout();
      return false;
    }
  };

  // 初期化処理（アプリロード時の認証状態復元）
  useEffect(() => {
    const initAuth = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        const userId = localStorage.getItem('userId');
        const userName = localStorage.getItem('userName');
        const userRole = localStorage.getItem('userRole');
        const userRoleId = localStorage.getItem('userRoleId');
        const userDepartment = localStorage.getItem('userDepartment');
        
        if (!token || !userId) {
          // 認証情報がなければ初期化を終了
          return;
        }
        
        // 保存された情報からユーザー状態を復元
        setCurrentUser({
          id: userId,
          name: userName,
          role: userRole,
          role_id: userRoleId,
          department: userDepartment
        });
        
        // トークンの検証（オプション、バックグラウンドで実行）
        // 期限切れのトークンは自動的に更新を試みる
        setTimeout(() => {
          validateToken();
        }, 0);
        
      } catch (err) {
        console.error('認証初期化エラー:', err);
      } finally {
        setLoading(false);
      }
    };

    // トークン検証関数
    const validateToken = async () => {
      try {
        // 簡単なトークン検証（JWTの期限チェックなど）
        const token = localStorage.getItem('accessToken');
        if (!token) return false;
        
        // JWTのペイロード部分をデコード
        const payload = parseJwt(token);
        
        // 期限切れチェック
        if (payload && payload.exp) {
          // expはUNIX時間（秒）
          const expirationTime = payload.exp * 1000; // ミリ秒に変換
          const currentTime = Date.now();
          
          // 期限切れまたは期限切れ間近（5分以内）の場合は更新
          if (expirationTime < currentTime + 5 * 60 * 1000) {
            await refreshToken();
          }
        }
        
        return true;
      } catch (err) {
        console.error('トークン検証エラー:', err);
        return false;
      }
    };

    // JWT解析のヘルパー関数
    const parseJwt = (token) => {
      try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        return JSON.parse(window.atob(base64));
      } catch (e) {
        return null;
      }
    };

    initAuth();
  }, []);

  // コンテキスト値の作成
  const value = {
    currentUser,
    setCurrentUser,
    loading,
    error,
    login,
    logout,
    refreshToken,
    isAuthenticated: !!currentUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};